import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ContactAPI } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, Mail, Flame, MapPin, Clock, MessageCircle, Facebook, Instagram,
  Send, ShieldCheck, HeartHandshake,
} from "lucide-react";

const REASONS = [
  "Purchase / Payment help",
  "Redeem code issue",
  "Bug report",
  "Feature request",
  "Bulk / partner pricing",
  "General question",
];

export default function ContactPage() {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [reason, setReason] = React.useState(REASONS[0]);
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await ContactAPI.submit(name, email, `[${reason}] ${msg}`);
      toast({ title: "Message sent", description: "We reply within 1 business day — usually much faster." });
      setName(""); setEmail(""); setMsg("");
    } catch (err) {
      toast({ title: "Send failed", description: apiErrorMessage(err, "Try again"), variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="grain">
      <Navbar />
      <section className="relative py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="chip mx-auto"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Contact</span>
            <h1 className="display-font mt-3 text-5xl font-bold tracking-tight sm:text-6xl">Talk to a human at <span className="ember-text">Furnace</span>.</h1>
            <p className="mt-3 text-muted-foreground">A real person reads every message. We aim for a 1-hour response during business hours, and always inside 24 hours.</p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
            <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <div><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 h-11" placeholder="Alex Printer" /></div>
                <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11" placeholder="you@shop.com" /></div>
              </div>
              <div className="mt-5">
                <Label>What's this about?</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-5">
                <Label>Message</Label>
                <Textarea rows={6} value={msg} onChange={(e) => setMsg(e.target.value)} required className="mt-1" placeholder="Tell us what you're working on…" />
              </div>
              <Button type="submit" disabled={busy} className="btn-ember mt-6 h-11 gap-2 font-semibold">
                <Send className="h-4 w-4" /> {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
              </Button>
              <p className="mt-3 text-[11px] text-muted-foreground">We never share your email. See our privacy stance in the FAQ.</p>
            </form>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-[#4aa4ff]" /><h3 className="display-font text-lg font-bold">Direct email</h3></div>
                <a href="mailto:hello@furnacedesign.lab" className="mt-2 block break-all text-sm text-[#4aa4ff] hover:underline">hello@furnacedesign.lab</a>
                <p className="mt-1 text-xs text-muted-foreground">Payment questions, refunds, bulk pricing, RIP integration — anything.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><Clock className="h-5 w-5 text-[#f5c845]" /><h3 className="display-font text-lg font-bold">Hours</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">Mon–Fri, 9am–6pm ET</p>
                <p className="mt-1 text-xs text-muted-foreground">Weekend responses within 24 hours.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-[#4aa4ff]" /><h3 className="display-font text-lg font-bold">Based in</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">United States · remote-first team</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><MessageCircle className="h-5 w-5 text-[#4aa4ff]" /><h3 className="display-font text-lg font-bold">Community</h3></div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href="#" className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs text-foreground hover:border-[#4aa4ff]/60"><Facebook className="h-3.5 w-3.5" /> Facebook Group</a>
                  <a href="#" className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs text-foreground hover:border-[#4aa4ff]/60"><Instagram className="h-3.5 w-3.5" /> Instagram</a>
                </div>
              </div>

              <div className="rounded-2xl chrome-border p-6">
                <div className="flex items-center gap-3"><HeartHandshake className="h-5 w-5 text-[#f5c845]" /><h3 className="display-font text-lg font-bold">Bulk / partner pricing?</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">Print farms and franchises: reach out for team seats and volume codes.</p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#4aa4ff]" /><h3 className="display-font text-lg font-bold">Refund policy</h3></div>
                <p className="mt-2 text-sm text-muted-foreground">15-day money-back guarantee, no forms. Email and we refund it.</p>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
